/**
 * OT&E business test ("Chạy thử nghiệp vụ OT&E"): every domain operation once against the
 * connection, run by the server in the background, on the OT&E server only (ADR-018: the
 * production server refuses it). The dialog chooses the
 * steps (a step selects what it needs; delete is opt-in); the run view follows the steps as they
 * finish, with what the registry answered to each command.
 */

import { CircleCheck, CircleDashed, CircleHelp, CircleX, FlaskConical, LoaderCircle, Play } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { api } from '../../api';
import {
  EXERCISE_STEP_REQUIRES,
  EXERCISE_STEPS,
  type ConnectionDetail,
  type ExerciseCommand,
  type ExerciseRun,
  type ExerciseStep,
  type ExerciseStepResult,
  type IntegrationEnvironment,
} from '../../api/areas/integrations';
import { Badge, type Tone } from '../../components/Badge';
import { Callout } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { Loading } from '../../components/States';
import { useRetained, useWrite } from '../../components/Write';
import { formatCount, formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { ChangeDialog, EnvironmentBanner, humanize, IntegrationError, Panel, serviceLabel, StaffName, tx, useIntegrationContext, When } from './shared';

const POLL_MS = 1500;

const RUN_TONE: Record<ExerciseRun['status'], Tone> = { RUNNING: 'pending', PASSED: 'ok', FAILED: 'crit', ABANDONED: 'warn' };

function RunStatusBadge({ status }: { status: ExerciseRun['status'] }) {
  const { t } = useTranslation('integrations');
  return <Badge tone={RUN_TONE[status]}>{t(`exercise.status.${status}`)}</Badge>;
}

/** The further along of two reads of the same run. */
function newer(a: ExerciseRun, b: ExerciseRun): ExerciseRun {
  if (a.status !== 'RUNNING') return a;
  if (b.status !== 'RUNNING') return b;
  return b.steps.length > a.steps.length ? b : a;
}

/** Re-reads a RUNNING run until it ends (a failed read is retried after the same pause). */
function useLiveRun(connectionId: string, initial: ExerciseRun | undefined): ExerciseRun | undefined {
  const [fetched, setFetched] = useState<ExerciseRun>();
  const run = initial && fetched?.id === initial.id ? newer(fetched, initial) : initial;
  useEffect(() => {
    if (!run || run.status !== 'RUNNING') return undefined;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      api.getOteExercise(connectionId, run.id, { signal: controller.signal }).then(setFetched, () => {
        if (!controller.signal.aborted) setFetched({ ...run });
      });
    }, POLL_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [connectionId, run]);
  return run;
}

// ---------------------------------------------------------------------------
// Facts and commands

function factValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return tx(value ? 'common.yes' : 'common.no', value ? 'yes' : 'no');
  if (typeof value === 'string') return /^\d{4}-\d{2}-\d{2}T/.test(value) ? formatDateTime(value) : value;
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    return value.length
      ? value.map((v) => (v && typeof v === 'object' && 'type' in v && 'handle' in v ? `${String(v.type)}: ${String(v.handle)}` : factValue(v))).join(', ')
      : '—';
  }
  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([k, v]) => `${tx(`exercise.fact.${k}`, humanize(k))}: ${factValue(v)}`)
      .join(' · ');
  }
  return String(value);
}

function Facts({ facts }: { facts: ExerciseStepResult['facts'] }) {
  const entries = facts && typeof facts === 'object' ? Object.entries(facts).filter(([, v]) => v !== null && v !== undefined) : [];
  if (!entries.length) return null;
  return (
    <dl className="grid gap-x-3 gap-y-0.5 text-xs sm:grid-cols-[max-content_1fr]">
      {entries.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-muted-foreground">{tx(`exercise.fact.${k}`, humanize(k))}</dt>
          <dd className="min-w-0 break-words">{factValue(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

function Command({ c }: { c: ExerciseCommand }) {
  const { t } = useTranslation('integrations');
  return (
    <li className="space-y-0.5 rounded border px-2 py-1.5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className={cn('font-mono', c.outcome !== 'SUCCESS' && 'text-critical')}>
          {t('exercise.commandLine', { command: c.command, outcome: t(`exercise.commandOutcome.${c.outcome}`) })}
          {c.result_code !== null ? ` · ${t('exercise.resultCode', { code: c.result_code })}` : ''}
        </span>
        <span className="text-muted-foreground">{formatCount(c.duration_ms)} ms</span>
      </div>
      {c.message ? <p className="break-words">{c.message}</p> : null}
      <p className="break-all text-muted-foreground">
        {c.sv_trid ? `${t('exercise.svTrid')}: ${c.sv_trid} · ` : ''}
        {t('exercise.clTrid')}: {c.cl_trid}
      </p>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Run view

const STEP_ICON = { PASSED: CircleCheck, FAILED: CircleX, UNKNOWN: CircleHelp, SKIPPED: CircleDashed } as const;
const STEP_COLOUR = { PASSED: 'text-success', FAILED: 'text-critical', UNKNOWN: 'text-warning', SKIPPED: 'text-muted-foreground' } as const;

function StepRow({ step, result, active }: { step: ExerciseStep; result: ExerciseStepResult | undefined; active: boolean }) {
  const { t } = useTranslation('integrations');
  const [open, setOpen] = useState(false);
  const Icon = result ? STEP_ICON[result.status] : active ? LoaderCircle : CircleDashed;
  const status = result ? t(`exercise.stepStatus.${result.status}`) : t(`exercise.stepStatus.${active ? 'ACTIVE' : 'PENDING'}`);
  return (
    <li className="flex gap-2 rounded-md border px-3 py-2">
      <Icon className={cn('mt-0.5 size-4 shrink-0', result ? STEP_COLOUR[result.status] : 'text-muted-foreground', active && !result && 'animate-spin')} aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
          <span className="font-medium">{t(`exercise.step.${step}`)}</span>
          <span className="text-xs text-muted-foreground">
            {status}
            {result ? ` · ${formatCount(result.duration_ms)} ms` : ''}
          </span>
        </div>
        {result?.note ? (
          <p className={cn('text-xs', result.status === 'PASSED' || result.status === 'SKIPPED' ? 'text-muted-foreground' : 'text-critical')}>
            {t(`exercise.note.${result.note}`, { step: result.blocked_by ? t(`exercise.step.${result.blocked_by}`) : '' })}
          </p>
        ) : null}
        {result ? <Facts facts={result.facts} /> : null}
        {result?.commands.length ? (
          <div className="text-xs">
            <button type="button" className="font-medium text-primary hover:underline" aria-expanded={open} onClick={() => setOpen(!open)}>
              {open ? t('exercise.hide') : t('exercise.commands')} ({result.commands.length})
            </button>
            {open ? (
              <ol className="mt-1 grid gap-1">
                {result.commands.map((c) => (
                  <Command key={c.cl_trid} c={c} />
                ))}
              </ol>
            ) : null}
          </div>
        ) : null}
      </div>
    </li>
  );
}

export function ExerciseRunView({ run }: { run: ExerciseRun }) {
  const { t } = useTranslation('integrations');
  const firstPending = run.selected_steps.find((s) => !run.steps.some((r) => r.step === s));
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <RunStatusBadge status={run.status} />
        <span>
          {t('exercise.domain')}: <code className="font-mono">{run.domain_name}</code>
        </span>
        <span className="text-xs text-muted-foreground">
          {t('exercise.progress', { done: run.steps.length, total: run.selected_steps.length })} · <When at={run.started_at} />
          {run.duration_ms !== null ? ` · ${formatCount(run.duration_ms)} ms` : ''}
        </span>
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          {t('exercise.by')} <StaffName staff={run.requested_by} />
        </span>
      </div>
      {run.status === 'ABANDONED' ? <Callout tone="warn">{t('exercise.abandonedHint')}</Callout> : null}
      <ol className="grid gap-1.5">
        {run.selected_steps.map((step) => (
          <StepRow key={step} step={step} result={run.steps.find((r) => r.step === step)} active={run.status === 'RUNNING' && step === firstPending} />
        ))}
      </ol>
      <p className="text-xs text-muted-foreground" title={`${run.probe.service} · ${run.probe.instance}`}>
        {t('exercise.probe', { service: serviceLabel(run.probe.service) })}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialog: choose the steps, start, follow

/** Everything `step` needs, transitively. */
function needs(step: ExerciseStep): ExerciseStep[] {
  return EXERCISE_STEP_REQUIRES[step].flatMap((r) => [r, ...needs(r)]);
}

function StepChoice({ step, checked, onChange }: { step: ExerciseStep; checked: boolean; onChange: (on: boolean) => void }) {
  const { t } = useTranslation('integrations');
  const id = useId();
  const required = EXERCISE_STEP_REQUIRES[step];
  return (
    <div className="flex gap-2 text-sm">
      <input id={id} type="checkbox" className="mt-1" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <label htmlFor={id} className="cursor-pointer">
        {t(`exercise.step.${step}`)}
        {required.length ? <span className="ml-1 text-xs text-muted-foreground">({t('exercise.needs', { list: required.map((r) => tx(`exercise.stepShort.${r}`, r)).join(', ') })})</span> : null}
        {step === 'DELETE' ? <span className="block text-xs text-muted-foreground">{t('exercise.deleteHint')}</span> : null}
      </label>
    </div>
  );
}

function ExerciseForm({ connection, onStarted, onClose }: { connection: ConnectionDetail; onStarted: () => void; onClose: () => void }) {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  const [selected, setSelected] = useState<ReadonlySet<ExerciseStep>>(() => new Set(EXERCISE_STEPS.filter((s) => s !== 'DELETE')));
  const write = useWrite((steps: ExerciseStep[], opts) => api.startOteExercise(connection.id, { steps }, opts));
  const run = useLiveRun(connection.id, write.state.phase === 'done' ? write.state.result : undefined);
  const toggle = (step: ExerciseStep, on: boolean) => {
    const next = new Set(selected);
    if (on) [step, ...needs(step)].forEach((s) => next.add(s));
    else EXERCISE_STEPS.filter((s) => s === step || needs(s).includes(step)).forEach((s) => next.delete(s));
    setSelected(next);
  };
  const start = async () => {
    const res = await write.run(EXERCISE_STEPS.filter((s) => selected.has(s)));
    if (res) onStarted();
  };
  const running = run?.status === 'RUNNING';
  return (
    <div className="space-y-4">
      <EnvironmentBanner env="OTE" message={t('exercise.banner')} />
      {run ? (
        <div className="rounded-lg border p-3">
          <ExerciseRunView run={run} />
        </div>
      ) : (
        <fieldset className="grid gap-2">
          <legend className="mb-1 text-sm font-medium">{t('exercise.stepsLabel')}</legend>
          {EXERCISE_STEPS.map((s) => (
            <StepChoice key={s} step={s} checked={selected.has(s)} onChange={(on) => toggle(s, on)} />
          ))}
        </fieldset>
      )}
      <p className="text-xs text-muted-foreground">{t('exercise.limits', { seconds: ctx.test_limits.ote_exercise_interval_seconds, perHour: ctx.test_limits.ote_exercise_per_hour })}</p>
      {write.state.phase === 'error' ? <IntegrationError error={write.state.error} /> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common.close')}
        </Button>
        {run ? (
          <Button type="button" onClick={write.reset} disabled={running}>
            <Play /> {running ? t('exercise.running') : t('exercise.runAgain')}
          </Button>
        ) : (
          <Button type="button" onClick={() => void start()} disabled={write.busy || selected.size === 0}>
            <Play /> {write.busy ? t('exercise.starting') : t('exercise.run')}
          </Button>
        )}
      </DialogFooter>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel on the connection's Tests tab

function RunList({ connectionId, runs }: { connectionId: string; runs: readonly ExerciseRun[] }) {
  const { t } = useTranslation('integrations');
  const [open, setOpen] = useState<string | null>(null);
  return (
    <ul className="grid gap-2">
      {runs.map((r) => (
        <li key={r.id} className="rounded-md border">
          <button type="button" className="flex w-full flex-wrap items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50" aria-expanded={open === r.id} onClick={() => setOpen(open === r.id ? null : r.id)}>
            <RunStatusBadge status={r.status} />
            <code className="font-mono text-xs">{r.domain_name}</code>
            <span className="text-xs text-muted-foreground">
              <When at={r.started_at} /> · {r.requested_by.display_name}
            </span>
            <span className="ml-auto text-xs text-primary">{open === r.id ? t('exercise.hide') : t('exercise.show')}</span>
          </button>
          {open === r.id ? (
            <div className="border-t p-3">
              <LiveRunView connectionId={connectionId} run={r} />
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function LiveRunView({ connectionId, run }: { connectionId: string; run: ExerciseRun }) {
  const live = useLiveRun(connectionId, run);
  return live ? <ExerciseRunView run={live} /> : null;
}

export function ExercisePanel({ connection, env }: { connection: ConnectionDetail; env: IntegrationEnvironment }) {
  const { t } = useTranslation('integrations');
  const [dialog, setDialog] = useState(false);
  const [tick, setTick] = useState(0);
  const ote = env === 'OTE';
  const runs = useApi((signal) => (ote ? api.listOteExercises(connection.id, { limit: 10 }, { signal }) : Promise.resolve({ items: [] })), [connection.id, ote, tick]);
  const data = useRetained(runs.data);
  const [latest, ...earlier] = data?.items ?? [];
  return (
    <Panel
      title={t('exercise.title')}
      description={t('exercise.description')}
      actions={
        <Button size="sm" onClick={() => setDialog(true)} disabled={!ote || connection.status === 'RETIRED'}>
          <FlaskConical /> {t('exercise.run')}
        </Button>
      }
    >
      {!ote ? (
        <Callout tone="info">{t('exercise.oteOnly')}</Callout>
      ) : runs.status === 'error' && !data ? (
        <DataError error={runs.error} onRetry={runs.reload} />
      ) : !data ? (
        <Loading rows={2} />
      ) : !latest ? (
        <p className="text-sm text-muted-foreground">{t('exercise.none')}</p>
      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">{t('exercise.latest')}</h3>
            <div className="rounded-lg border p-3">
              <LiveRunView key={latest.id} connectionId={connection.id} run={latest} />
            </div>
          </div>
          {earlier.length ? (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">{t('exercise.history')}</h3>
              <RunList connectionId={connection.id} runs={earlier} />
            </div>
          ) : null}
        </div>
      )}
      <ChangeDialog
        open={dialog}
        onOpenChange={(o) => {
          setDialog(o);
          if (!o) setTick((n) => n + 1);
        }}
        title={t('exercise.dialogTitle', { name: connection.name })}
        description={t('exercise.dialogDescription')}
        wide
      >
        <ExerciseForm
          connection={connection}
          onStarted={() => setTick((n) => n + 1)}
          onClose={() => {
            setDialog(false);
            setTick((n) => n + 1);
          }}
        />
      </ChangeDialog>
    </Panel>
  );
}
